/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { ChangeEvent, FC, useCallback } from "react";
import { IFieldValidationError } from "../FieldTypes";
import { FieldWrapper } from "../FieldWrapper";
import { Icon, ICON_SIZE, InputAdornment, InputButton } from "../../core";
import { FieldInput } from "../FieldInput";
import { isFunction, isNil, isNilOrEmptyString } from "@adapptio/lib-shared";
import { FieldStatus } from "../FieldStatus";

export interface IFieldNumberProps {
	id?: string;
	label?: string;
	value: number;
	placeholder?: string;
	readOnly?: boolean;
	disabled?: boolean;
	min?: number;
	max?: number;
	step?: string;
	validationErrors?: IFieldValidationError[];
	hideButtons?: boolean;
	className?: string;
	onChange?: (value: number, ev: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	suffix?: string;
	debounceTimeout?: number;
	clearable?: boolean;
	startAdornment?: React.ReactNode;
}

export const FieldNumber: FC<IFieldNumberProps> = ({
	id,
	value,
	min,
	max,
	step,
	placeholder,
	readOnly,
	disabled,
	validationErrors,
	className,
	grow,
	onChange,
	onBlur,
	hideButtons,
	inputRef,
	suffix,
	debounceTimeout,
	clearable,
	startAdornment
}) => {
	const baseClass = BEM("edt-field-number");

	const adornment = validationErrors?.length > 0 || clearable || !hideButtons;

	const handleChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			if (onChange) {
				onChange(ev.target.valueAsNumber, ev);
			}
		},
		[ onChange ]
	);

	const increment = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(ev) => {
			if (isFunction(onChange)) {
				const newValue = (value || 0) + 1;
				onChange(Math.min(newValue, isNil(max) ? Infinity : max), ev as unknown as ChangeEvent<HTMLInputElement>); // hack typing
			}
		},
		[ onChange, value ]
	);

	const decrement = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(ev) => {
			if (isFunction(onChange)) {
				const newValue = (value || 0) - 1;
				onChange(Math.max(newValue, isNil(min) ? -Infinity : min), ev as unknown as ChangeEvent<HTMLInputElement>); // hack typing
			}
		},
		[ onChange, value ]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null, null);
		}
	}, [ onChange ]);

	return (
		<FieldWrapper type="text" className={className} grow={grow}>
			<FieldInput
				id={id}
				type="number"
				inputMode="numeric"
				className={baseClass.toClassName()}
				value={value}
				suffix={suffix}
				placeholder={placeholder}
				onChange={handleChange}
				onBlur={onBlur}
				min={min}
				max={max}
				step={step}
				inputRef={inputRef}
				readOnly={readOnly}
				disabled={disabled}
				debounceTimeout={debounceTimeout}
				startAdornment={startAdornment}
				endAdornment={
					adornment && (
						<InputAdornment>
							{validationErrors?.length > 0 && <FieldStatus errors={validationErrors} />}
							{clearable && (
								<InputButton
									className={baseClass.element("editor-clear").toClassName()}
									onClick={handleClearClick}
									tabIndex={-1}
									disabled={isNilOrEmptyString(value)}
								>
									<Icon
										className={baseClass.element("editor-clear-icon").toClassName()}
										name="mdi/close"
									/>
								</InputButton>
							)}
							{!hideButtons && (
								<>
									<InputButton
										type="button"
										className={baseClass.element("control").toClassName()}
										onClick={decrement}
										disabled={readOnly || (!isNil(min) && value <= min)}
										tabIndex={-1}
										aria-controls={id}
									>
										<Icon
											name="mdi/minus"
											className={baseClass.element("control-icon").toClassName()}
											size={ICON_SIZE.XSMALL}
										/>
									</InputButton>
									<InputButton
										type="button"
										className={baseClass.element("control").toClassName()}
										onClick={increment}
										disabled={readOnly || (!isNil(max) && value >= max)}
										tabIndex={-1}
										aria-controls={id}
									>
										<Icon
											name="mdi/plus"
											className={baseClass.element("control-icon").toClassName()}
											size={ICON_SIZE.XSMALL}
										/>
									</InputButton>
								</>
							)}
						</InputAdornment>
					)
				}
			/>
		</FieldWrapper>
	);
};
